import { Td, Tr } from 'app/components/Table';
import { useState } from 'react';
import { Row } from '@tanstack/react-table';
import { ListItem, ListItemText } from 'app/components/ChakraTableList';
import { Button } from '@chakra-ui/react';
import { Connection } from 'types/profile';
import { Link } from 'types/api.types';
import { ReleaseCover } from 'app/components/Releases/ReleaseCover';
import { ReleaseType } from 'types/releases';

interface ConnectionsTableRowProps {
  row: Row<Connection>;
  onConnect: (providerId: string) => Promise<void>;
  onDisconnect: (
    disconnectLink: Link,
    guid: string,
    name: string,
    providerId: string
  ) => Promise<void>;
}

export const ConnectionsTableRow = ({
  row,
  onConnect,
  onDisconnect,
}: ConnectionsTableRowProps) => {
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { guid, providerId, name, _links } = row.original;

  const type =
    providerId === 'SoundCloud'
      ? ReleaseType.SOUNDCLOUD
      : providerId === 'TuneCore'
      ? ReleaseType.TUNECORE
      : providerId === 'Connex'
      ? ReleaseType.LABEL_V2
      : undefined;

  const connectionText =
    providerId === 'TuneCore'
      ? 'Create and export a release for distribution via TuneCore'
      : `Release your music to ${providerId}`;

  return (
    <Tr zIndex={1} position="relative" role="row" data-group>
      <Td isAvatar sx={{ overflow: 'visible' }}>
        {type && <ReleaseCover size="small" type={type} />}
      </Td>
      <Td sx={{ pl: 0 }}>
        <ListItemText
          data-testid="connection-type"
          primary={providerId}
          secondary={name}
          secondaryTextProps={{
            color: 'textSubdued',
            fontSize: 'xs',
            'data-testid': 'connection-name',
          }}
        />
      </Td>
      <Td>
        <ListItemText data-testid="connection-text" primary={connectionText} />
      </Td>
      <Td />
      <Td>
        <ListItem>
          {!guid && (
            <Button
              size="sm"
              flex="0 0 100px"
              fontSize="sm"
              onClick={() => {
                onConnect(providerId);
              }}
            >
              Connect
            </Button>
          )}
          {guid && _links?.disconnect && (
            <Button
              isDisabled={isDisconnecting}
              variant="secondary"
              size="sm"
              flex="0 0 100px"
              fontSize="sm"
              onClick={async () => {
                if (_links.disconnect) {
                  setIsDisconnecting(true);
                  await onDisconnect(
                    _links.disconnect,
                    guid,
                    name || providerId,
                    providerId
                  );
                  setIsDisconnecting(false);
                }
              }}
            >
              Disconnect
            </Button>
          )}
        </ListItem>
      </Td>
    </Tr>
  );
};
